import type EventBase from '@/model/EventClass/EventBase'
import EventID from '@/model/EventClass/EventID'
import {
  isForcedSaveReadSlotValid,
} from '@/features/event/forcedSaveRead'
import { GoodsTypeEnum } from '@/model/MallNewClass/Goods'
import { ED } from './eventDescriptionMessages'
import { formatStoryMapId } from '@/features/story/storyTreeProjection'
import { getEventDescriptionProject } from './eventDescriptionContext'
import {
  bubbleLayDescription,
  callMenuInputBoxDescription,
  fileNameEx,
  firstLineCs,
  normalizeGifActionDescription,
  pad4,
  personLocationZh,
  textToNullRef,
} from './eventDescriptionUtil'
import { IFBranch, inferIfBranch } from './eventDescriptionInferIf'

/**
 * 事件列表描述生成模块。
 *
 * 橙光事件的业务数据主要存放在 `EventBase.Parameter` 字符串数组中，本模块负责按事件 Code
 * 解释这些参数，并生成左侧事件列表里展示的中文摘要。部分摘要需要读取当前 `MainProject`
 * 中的变量名、剧情名、商城商品、自定义 UI、气泡角色等上下文数据。
 */

/** 只需要展示固定事件名，或解析失败时用于兜底显示的事件名称表。 */
const EVENT_NAME_BY_CODE: Record<number, string> = {
  [-1]: '不能选',
  0: '添加项',
  100: '显示文章',
  101: '剧情分歧',
  1010: '剧情分歧EX',
  1011: '剧情分歧EX2',
  102: '剧情分歧结束',
  103: '自动播放剧情',
  104: '快进剧情',
  105: '数值输入',
  106: '提示消息框',
  107: '注释',
  108: '分支选项内容',
  109: '消失对话框',
  110: '打开指定网页',
  111: '禁用/开启菜单',
  112: '悬浮组件开关',
  150: '刷新UI画面',
  151: '返回游戏界面',
  200: '条件分歧',
  201: '条件分歧结束',
  202: '循环',
  203: '以上反复',
  204: '按钮分歧',
  205: '按钮分歧结束',
  206: '跳转剧情',
  2062: '跳过剧情',
  2063: '高级UI跳转剧情',
  207: '数值操作',
  208: '返回标题画面',
  209: '中断循环',
  210: '等待',
  211: '除此之外的场合',
  212: '按钮分歧内容',
  213: '二周目变量',
  214: '呼叫游戏界面',
  215: '字符串',
  216: '高级数值操作',
  217: '高级条件分歧',
  218: '强制存读档',
  219: '气泡式效果',
  220: '气泡模式',
  221: '发射气泡',
  222: '实例操作',
  223: '数组操作',
  2231: '数组元素操作',
  230: '显示广告',
  231: '广告分支',
  232: '广告结束',
  251: '呼叫子剧情',
  252: '体力锁',
  253: '剧情锁',
  260: '呼叫Scene',
  301: '天气',
  302: '震动',
  303: '画面闪烁',
  304: '准备转场',
  305: '转场开始',
  306: '更改场景色调',
  307: '插入BGM鉴赏',
  308: '插入CG鉴赏',
  309: '解锁收集项',
  310: '简易特效',
  400: '显示图片',
  401: '销毁图层',
  402: '移动图片',
  403: '显示心情',
  404: '旋转图片',
  405: '资源预加载',
  406: '显示动态图片',
  4062: '动态图片2.0',
  407: '图片遮罩',
  408: '换装角色',
  501: '播放背景音乐',
  502: '播放音效',
  503: '播放语音',
  504: '播放循环音效',
  505: '淡出背景音乐',
  506: '停止音效',
  507: '停止语音',
  508: '淡出循环音效',
  602: '显示视频',
  603: '视频节点',
  604: '视频播放完成',
  605: '视频选项',
  606: '视频选项内容',
  607: '视频选项结束',
  700: '显示视频',
  701: '书签',
}

/**
 * 生成无需深度解析的事件名称。
 * @param code 事件 Code。
 * @param par 事件参数数组；部分事件会把第 0 或第 1 项描述追加到事件名后。
 * @returns 可直接显示在事件列表中的名称。
 */
function describeKnownEventName(code: number, par: string[]): string {
  const name = EVENT_NAME_BY_CODE[code]
  if (!name) return ''
  const desc = String(par[0] ?? par[1] ?? '').trim()
  if (!desc) return name
  if ([2062, 222, 223, 2231, 260, 602, 603, 604, 605, 606, 607, 700].includes(code)) {
    return `${name}：${desc}`
  }
  return name
}

/**
 * 高级数值事件的第 4 位参数会混入扩展信息，这里只截取用户可读的描述片段。
 * @param par 事件参数数组。
 * @returns 高级数值操作描述。
 */
function seniorVarDesc(par: string[]): string {
  let d = par[4] ?? ''
  if (d.includes('|&;')) d = d.split('|&;')[0] ?? ''
  return d
}

/**
 * 将变量下标转成“数值1[变量名]”这类显示标签。
 * @param index 0 基变量下标。
 * @param names 当前工程中的变量名列表。
 * @param kind 变量类型文案。
 * @param isIndex 是否显示为索引变量。
 * @returns 变量显示标签。
 */
function getNamedVariableLabel(index: number, names: string[], kind: '数值' | '二周目' | '字符串', isIndex = false) {
  const safeIndex = Number.isFinite(index) && index >= 0 ? index : 0
  const name = names[safeIndex] ?? ''
  const label = `${kind}${safeIndex + 1}${name ? `[${name}]` : ''}`
  return isIndex ? `${label}(索引)` : label
}

/** 将 0 基变量下标转成事件描述使用的三位序号。 */
function formatVariableOrder(index: number): string {
  const safeIndex = Number.isFinite(index) && index >= 0 ? index : 0
  return String(safeIndex + 1).padStart(3, '0')
}

/**
 * 获取变量显示名，未配置时使用“未命名”保持列表可读。
 * @param index 0 基变量下标。
 * @param names 当前工程中的变量名列表。
 * @returns 变量名称。
 */
function getEventVariableName(index: number, names: string[]): string {
  const safeIndex = Number.isFinite(index) && index >= 0 ? index : 0
  return String(names[safeIndex] ?? '').trim() || '未命名'
}

/** 生成普通数值变量的事件描述标签。 */
function getEventVariableSlotLabel(index: number, names: string[]): string {
  return `[${formatVariableOrder(index)}:${getEventVariableName(index, names)}]`
}

/** 生成“数值索引变量”的事件描述标签。 */
function getEventVariableIndexLabel(index: number, names: string[]): string {
  return `[数值索引${formatVariableOrder(index)}:${getEventVariableName(index, names)}]`
}

/**
 * 按剧情 ID 读取当前工程中的剧情名。
 * @param mapId 剧情 ID。
 * @returns 剧情名；找不到时返回空字符串。
 */
function getStoryDisplayLabel(mapId: number) {
  const proj = getEventDescriptionProject()
  const maps = proj?.Storys?.() ?? []
  const matched = maps.find((item) => Number(item?.Id ?? 0) === Number(mapId))
  if (!matched) return ''
  return String(matched.Name ?? '').trim()
}

/** 格式化剧情锁商品价格，兼容鲜花和丸子两种价格。 */
function formatStoryLockGoodsPrice(price: number, pakoras: number): string {
  const flowerText = `${Math.max(0, Number(price) || 0)}花`
  const pakorasValue = Math.max(0, Number(pakoras) || 0)
  return pakorasValue > 0 ? `${flowerText}/${pakorasValue}丸子` : flowerText
}

/**
 * 生成剧情锁事件摘要。
 * @param par 剧情锁事件参数，第 0 项为商城商品 ID。
 * @returns 剧情锁商品名称、ID 和价格。
 */
function describeStoryLock(par: string[]): string {
  const commodityId = parseInt(par[0] ?? '0', 10)
  const project = getEventDescriptionProject()
  const goods = project?.GameMallNew?.GoodsList?.find(
    (item) => Number(item.GoodsId) === commodityId && Number(item.GoodsType) === GoodsTypeEnum.Map,
  )
  if (goods) {
    const name = goods.GoodsName || '未命名商品'
    const price = formatStoryLockGoodsPrice(Number(goods.GoodsPrice), Number(goods.GoodsPricePakoras))
    return `剧情锁：${name} · ID:${goods.GoodsId} · ${price}`
  }
  return par[1] || (commodityId > 0 ? `剧情锁：ID:${commodityId}` : '剧情锁')
}

/**
 * 解析数值/二周目数值操作事件的左右操作数。
 * @param par 事件参数数组。
 * @param isVarEx 当前事件是否操作二周目数值。
 * @returns 类似 `[001:好感] += 随机(1~3)` 的描述。
 */
function parseVariableOperandLabel(par: string[], isVarEx = false) {
  const proj = getEventDescriptionProject()
  const varNames = (proj?.gameVar?.Data ?? []).map((item) => item?.Name ?? '')
  const varExNames = (proj?.gameVarEx?.Data ?? []).map((item) => item?.Name ?? '')
  const rawValue = par[3] ?? ''
  const rawIndex = rawValue.includes('|') ? rawValue.split('|')[1] : rawValue
  const type = parseInt(par[2] ?? '0', 10)
  const operator = ['=', '+=', '-=', '*=', '/=', '%='][parseInt(par[1] ?? '0', 10)] ?? '='
  const targetIndex = parseInt(par[0] ?? '0', 10)
  const isTargetIndex = par[5] === '1'
  const targetLabel = isTargetIndex
    ? getEventVariableIndexLabel(targetIndex, varNames)
    : getEventVariableSlotLabel(targetIndex, isVarEx ? varExNames : varNames)
  let valueLabel = rawValue
  if (type === 0) valueLabel = rawIndex
  else if (type === 1) valueLabel = getEventVariableSlotLabel(parseInt(rawIndex, 10), varNames)
  else if (type === 2) valueLabel = `随机(${rawValue.replace('|', '~')})`
  else if (type === 3) valueLabel = getEventVariableSlotLabel(parseInt(rawIndex, 10), varExNames)
  else if (type === 4) valueLabel = getEventVariableIndexLabel(parseInt(rawIndex, 10), varNames)
  else if (type === 5) valueLabel = `服务器时间(${rawValue})`
  else if (type === 6) valueLabel = '鲜花数'
  else if (type === 10) valueLabel = `本地时间(${rawValue})`
  return `${targetLabel} ${operator} ${valueLabel}`
}

/**
 * 生成“简易特效”事件的参数摘要。
 * @param par 事件参数数组。
 * @returns 图片编号、特效类型、强度、时长和循环信息。
 */
function simpleEffectLine(par: string[]): string {
  const picId = parseInt(par[0] ?? '0', 10)
  const effectNum = parseInt(par[1] ?? '0', 10)
  const strength = parseInt(par[2] ?? '0', 10)
  const isLoop = parseInt(par[3] ?? '0', 10)
  const isSecond = parseInt(par[4] ?? '0', 10)
  const duration = parseInt(par[5] ?? '0', 10)
  const sUnit = isSecond === 0 ? ' 帧' : ' 秒'
  const sLoop = isLoop === 0 ? '否' : '是'
  switch (effectNum) {
    case 0:
      return `图片：${picId}，无`
    case 1:
      return `图片：${picId}，马赛克，颗粒度：${strength}，${duration}${sUnit}`
    case 2:
      return `图片：${picId}，毛刺，程度：${strength}，${duration}${sUnit}，循环：${sLoop}`
    case 3:
      return `图片：${picId}，放大淡出，百分比：${strength}，${duration}${sUnit}，循环：${sLoop}`
    case 4:
      return `图片：${picId}，缩小后放大，百分比：${strength}，${duration}${sUnit}，循环：${sLoop}`
    case 5:
      return `图片：${picId}，高斯模糊，颗粒度：${strength}，${duration}${sUnit}`
    default:
      return ''
  }
}

/**
 * 生成音频播放事件的淡入后缀。
 * @param par 音频播放事件参数。
 * @returns 淡入帧数/秒数；未启用淡入时为空。
 */
function audioFadeSuffix(par: string[]): string {
  if (par.length <= 4) return ''
  if (par[4] !== '1') return ''
  if (par[6] === '1') {
    const precise = Number(par[7] ?? '')
    const seconds = Number.isFinite(precise) ? precise : (Number(par[5] ?? '0') || 0) / 60
    return `淡入 ${Number(seconds.toFixed(3))}秒`
  }
  return `淡入 ${par[5] ?? ''}帧`
}

/**
 * 将音轨参数转成“音轨1-4”的显示文案。
 * @param raw 音轨下标字符串。
 * @returns 音轨标签。
 */
function audioTrackLabel(raw: string | undefined): string {
  const index = Math.max(0, Math.min(3, parseInt(raw ?? '0', 10) || 0))
  return `音轨${index + 1}`
}

/**
 * 生成“显示图片”事件摘要。
 * @param par 图片显示事件参数。
 * @returns 图片层级、资源名、坐标、透明度和缩放信息。
 */
function describePicShow(par: string[]): string {
  const picId = parseInt(par[0] ?? '0', 10)
  let namePic = ''
  if (picId === 1) namePic = `${ED.YUANJINGTU}：1, `
  else if (picId === 2) namePic = `${ED.BEIJINGTU}：2, `
  else if (picId === 23) namePic = `${ED.QIANJINGTU}：23, `
  else namePic = `${ED.LIHUI}：${picId}, `
  const isUserStr = par[11] === '1'
  if (isUserStr) {
    const sid = parseInt(par[12] ?? '0', 10)
    namePic += `${ED.ZIFUCHUAN}：${sid + 1}, `
  } else {
    const pathLabel = fileNameEx(par[1])
    if (pathLabel) namePic += `${pathLabel}, `
  }
  const isAssigned = par[2] === '1'
  const x = parseInt(par[3] ?? '0', 10)
  const y = parseInt(par[4] ?? '0', 10)
  if (!isAssigned) namePic += `X${x},Y${y}, `
  else namePic += `X${x + 1},Y${y + 1}, `
  const op = parseInt(par[7] ?? '0', 10)
  const zx = parseInt(par[5] ?? '0', 10)
  const zy = parseInt(par[6] ?? '0', 10)
  namePic += `${ED.TOUMING}${op}, `
  namePic += `${zx}%, ${zy}% `
  return namePic
}

/**
 * 生成“换装角色”事件摘要。
 * @param par 换装角色事件参数。
 * @returns 角色编号、坐标、透明度和缩放信息。
 */
function describeDressRole(par: string[]): string {
  const picId = parseInt(par[0] ?? '0', 10)
  const isUserStr = par[1] === '1'
  const half = parseInt(par[2] ?? '0', 10)
  const isAssigned = par[3] === '1'
  const x = parseInt(par[4] ?? '0', 10)
  const y = parseInt(par[5] ?? '0', 10)
  const zx = parseInt(par[6] ?? '0', 10)
  const zy = parseInt(par[7] ?? '0', 10)
  const op = parseInt(par[8] ?? '0', 10)
  let nameDress = `${ED.HUANZHUANGJUESE}:  `
  nameDress += `${ED.BIANHAO}${picId},`
  if (isUserStr) nameDress += `${ED.ZIFUCHUAN}：${half + 1}, `
  else nameDress += `角色${half + 1},`
  if (!isAssigned) nameDress += `X${x},Y${y}, `
  else nameDress += `X${x + 1},Y${y + 1}, `
  nameDress += `${ED.TOUMING}${op}, `
  nameDress += `${zx}%, ${zy}% `
  return nameDress
}

/**
 * 生成“移动图片”事件摘要。
 * @param par 移动图片事件参数。
 * @param zhen 帧单位文案。
 * @returns 图片移动时长、坐标、透明度和缩放信息。
 */
function describeMovePic(par: string[], zhen: string): string {
  const picId = par[0] ?? ''
  const type = parseInt(par[11] ?? '0', 10)
  const frames = Math.max(0, Math.round(Number(par[9] ?? '0') || 0))
  const isAssigned = par[2] === '1'
  const x = parseInt(par[3] ?? '0', 10)
  const y = parseInt(par[4] ?? '0', 10)
  const op = parseInt(par[7] ?? '0', 10)
  const zx = Number(par[5] ?? '0') || 0
  const zy = Number(par[6] ?? '0') || 0
  let moveName = `${picId}, `
  if (type === 0) moveName += `${frames}${zhen}, `
  else moveName += `${Number((frames / 60).toFixed(3))}秒, `
  if (isAssigned) moveName += `X=v[${x + 1}], Y=v[${y + 1}], `
  else moveName += `X${x}, Y${y}, `
  moveName += `${ED.TOUMING}${op}, ${zx}%, ${zy}% `
  return `${ED.YIDONGTUPIAN}：${moveName}`
}

/**
 * 生成“旋转图片”事件摘要。
 * @param par 旋转图片事件参数。
 * @param zhen 帧单位文案。
 * @returns 图片编号、时长、中心点和角度信息。
 */
function describeRotatePic(par: string[], zhen: string): string {
  const picId = par[0] ?? ''
  const timeAssigned = par[1] === '1'
  const time = Math.max(0, Math.round(Number(par[2] ?? '0') || 0))
  const angleAssigned = par[3] === '1'
  const angle = Number(par[4] ?? '0') || 0
  const pointRate = parseInt(par[6] ?? '0', 10)
  const pointLabel = pointRate === 10 ? `自定义(${par[7] ?? '0,0'})` : pointRate === 1 ? '左上' : '中心'
  const type = parseInt(par[9] ?? '0', 10)
  const timeLabel = timeAssigned
    ? `v[${Math.round(time) + 1}]`
    : (type === 0 ? `${time}${zhen}` : `${Number((time / 60).toFixed(3))}秒`)
  const angleLabel = angleAssigned ? `v[${Math.round(angle) + 1}]` : `${angle}`
  return `${ED.XUANZHUANTUPIAN}：${picId}, ${timeLabel}, ${pointLabel}, ${angleLabel}`
}

/**
 * 生成动态图片/OAF 显示事件摘要。
 * @param par 动态图片事件参数。
 * @param isOaf 是否按 OAF 动画坐标规则解析。
 * @returns 图层、资源、动作、坐标、透明度和缩放信息。
 */
function describeGifOrOaf(par: string[], isOaf: boolean): string {
  const picId = par[0] ?? ''
  const isUserStr = par[11] === '1'
  let gif = `${picId}, `
  if (isUserStr) {
    const sid = parseInt(par[12] ?? '0', 10)
    gif += `${ED.ZIFUCHUAN}：${sid + 1}, `
  } else {
    gif += `${fileNameEx(par[1])}, `
  }
  const desc = par[9] ?? ''
  const marker = `${ED.DONGZUO}：`
  if (desc.includes(marker)) {
    gif += `${normalizeGifActionDescription(desc)}, `
  }
  const isAssigned = par[2] === '1'
  const x = parseInt(par[3] ?? '0', 10)
  const y = parseInt(par[4] ?? '0', 10)
  if (isOaf) {
    if (isAssigned) gif += `X=v[${x + 1}], Y=v[${y + 1}], `
    else gif += `X${x}, Y${y}, `
  } else {
    gif += `X${x}, Y${y}, `
  }
  const op = parseInt(par[7] ?? '0', 10)
  const zx = parseInt(par[5] ?? '0', 10)
  const zy = parseInt(par[6] ?? '0', 10)
  gif += `${ED.TOUMING}${op}, ${zx}%, ${zy}% `
  return `${ED.XIANSHIDONGTAITUPIAN}：${gif}`
}

/**
 * 生成“呼叫游戏界面”事件摘要。
 * @param par 呼叫界面事件参数，第 0 项为系统保留界面编号或自定义 UI 下标。
 * @returns 系统界面/自定义 UI 名称和淡入信息。
 */
function describeCallMenu(par: string[]): string {
  const num = parseInt(par[0] ?? '0', 10)
  let str = `[${ED.XITONGBAOLIU}]`
  switch (num) {
    case 10001:
      str += ED.YOUXICAIDAN
      break
    case 10002:
      str += ED.JVQINGHUIFANG
      break
    case 10003:
      str += `CG${ED.JIANSHANG}`
      break
    case 10004:
      str += `BGM${ED.JIANSHANG}`
      break
    case 10005:
      str += ED.CUNDANGJIEMIAN
      break
    case 10006:
      str += ED.DUDANGJIEMIAN
      break
    case 10007:
      str += ED.YOUXISHEZHI
      break
    case 10008:
      str += ED.LIKAIYOUXI
      break
    case 10009:
      str += ED.ZIDONGJVQING
      break
    case 10010:
      str += ED.YOUXISHANGCHENG
      break
    case 10011:
      str += ED.SHOUJIXITONG
      break
    case 10012:
      str += ED.XIANDINGSHANGCHENG
      break
    case 10013:
      str += ED.FULI
      break
    case 10014:
      str += ED.NAME_UI
      break
    case 10015:
      str += ED.DRESS_UI
      break
    default: {
      const p = getEventDescriptionProject()
      const list = p?.systemData?.customUI?.CUIList ?? []
      const index = num
      const name = index >= 0 && index < list.length ? list[index]?.Name ?? '' : ''
      str = `[${pad4(num)}]${name}`
      break
    }
  }

  if (par.length > 1) {
    if (par[2] === '1') {
      const ver = parseInt(par[1] ?? '0', 10)
      str += ` 淡入${ver / 60}秒`
    } else {
      str += ` 淡入${par[1] ?? ''}帧`
    }
  }

  if (num === 10014) {
    str += callMenuInputBoxDescription(par[3])
  }
  if (num === 10015) {
    const roleSel = parseInt(par[5] ?? '0', 10)
    str += ` 角色${roleSel + 1}`
  }

  return `${ED.HUJIAOYOUXIJIEMIAN}：${str}`
}

/**
 * 生成剧情跳转事件摘要。
 * @param par 跳转剧情事件参数。
 * @param label 跳转类型名称。
 * @returns 目标剧情名和淡出信息。
 */
function describeJumpStory(par: string[], label: string): string {
  const mapId = parseInt(par[0] ?? '0', 10)
  const idText = Number.isFinite(mapId) ? formatStoryMapId(mapId) : String(par[0] ?? '').trim()
  const storyName = getStoryDisplayLabel(mapId) || String(par[2] ?? '').trim()
  let base = storyName ? `${label}：${idText}：${storyName}` : `${label}：${idText}`
  if (par[1] !== '1') return base
  const frame = Math.max(0, Math.round(Number(par[3] ?? '0') || 0))
  return `${base}，画面淡出${frame}帧`
}

/**
 * 生成「呼叫子剧情」事件摘要：剧情 ID + 剧情名。
 * @param par 呼叫子剧情事件参数。
 */
function describeCallSubStory(par: string[]): string {
  const mapId = parseInt(par[0] ?? '0', 10)
  const idText = Number.isFinite(mapId) ? formatStoryMapId(mapId) : String(par[0] ?? '').trim()
  const storyName = getStoryDisplayLabel(mapId) || String(par[1] ?? '').trim()
  if (storyName) return `${ED.HUJIAOZIJVQING}：${idText}：${storyName}`
  return `${ED.HUJIAOZIJVQING}：${idText}`
}

/**
 * 生成条件分歧事件摘要。
 * @param par 条件分歧事件参数。
 * @param _code 事件 Code，保留参数用于后续区分 IF 子类型。
 * @returns 分歧条件的可读描述。
 */
function describeIf(par: string[], _code: EventID): string {
  const br = inferIfBranch(par)
  const rawDesc = String(par[5] ?? '').trim()
  const desc = rawDesc === '0' ? '' : rawDesc
  const head = `${ED.TIAOJIANFENQI}：`
  const proj = getEventDescriptionProject()
  const varNames = (proj?.gameVar?.Data ?? []).map((item) => item?.Name ?? '')
  const varExNames = (proj?.gameVarEx?.Data ?? []).map((item) => item?.Name ?? '')
  const relation = ['==', '>=', '<=', '>', '<', '!='][parseInt(par[1] ?? '0', 10)] ?? '=='
  const cmpType = parseInt(par[2] ?? '0', 10)
  const cmpValue = par[3] ?? ''
  const platformLabel = {
    1: 'PC',
    2: 'Web',
    3: 'Android',
    4: 'iOS',
    5: 'H5',
  }[parseInt(par[3] ?? '0', 10) as 1 | 2 | 3 | 4 | 5] ?? `平台${par[3] ?? '0'}`
  const triggerLabel = par[3] === '1' ? '按下' : '经过'
  const suffix = desc ? ` (${desc})` : ''
  const parseCompareValue = () => {
    if (cmpType === 1) return getNamedVariableLabel(parseInt(cmpValue, 10), varNames, '数值')
    if (cmpType === 2) return getNamedVariableLabel(parseInt(cmpValue, 10), varExNames, '二周目')
    return cmpValue || '0'
  }
  if (br === IFBranch.Pay) {
    const isRes = parseInt(par[1] ?? '0', 10) === 1
    const pid = par[2] ?? ''
    const mid = isRes
      ? `${ED.HUIFUGOUMAI} ${pid} ${ED.CHENGGONG}`
      : `${ED.CHANGSHIGOUMAI} ${pid} ${ED.CHENGGONG}`
    return `${head}${mid}${suffix}`
  }
  if (br === IFBranch.VarIndex) {
    const target = getNamedVariableLabel(parseInt(par[0] ?? '0', 10), varNames, '数值')
    return `${head}${target} ${relation} ${parseCompareValue()}${suffix}`
  }
  if (br === IFBranch.VarEx) {
    const index = parseInt((par[0] ?? '').split('|')[1] ?? '0', 10)
    const target = getNamedVariableLabel(index, varExNames, '二周目')
    return `${head}${target} ${relation} ${parseCompareValue()}${suffix}`
  }
  if (br === IFBranch.Flower) {
    return `${head}鲜花数 ${relation} ${parseCompareValue()}${suffix}`
  }
  if (br === IFBranch.Mouse) {
    if (parseInt(par[1] ?? '0', 10) === 0) {
      return `${head}鼠标${triggerLabel} 矩形区域(${par[2] ?? '0,0,100,100'})${suffix}`
    }
    return `${head}鼠标${triggerLabel} 图片${par[2] ?? '1'}${suffix}`
  }
  if (br === IFBranch.Platform) {
    return `${head}平台 = ${platformLabel}${suffix}`
  }
  if (br === null) return head
  return `${head}${desc || '未设置条件'}`
}

/**
 * 生成事件列表中显示的事件摘要。
 *
 * 该方法对齐 3.0 工具的 `GetDescriptionContent` 思路：优先按 `EventID` 解释
 * `EventBase.Parameter`，必要时读取当前工程上下文中的变量名、剧情名、资源名或系统设置。
 *
 * @param ev 待显示的事件对象。
 * @returns 事件列表摘要；解析异常时退回到固定事件名。
 */
export function describeEvent(ev: EventBase): string {
  const code = ev.Code as EventID
  const par = ev.Parameter ?? []
  const zhen = ED.ZHEN
  const yl = ED.YINLIANG
  const proj = getEventDescriptionProject()

  try {
    switch (code) {
      case EventID.AddItem:
        return '>'
      case EventID.Text: {
        const raw = par[2] ?? ''
        const first = firstLineCs(raw)
        const hasPath = !!(par[19] && String(par[19]).trim())
        const extra = par[16] ?? ''
        const advanced = !!(extra && String(extra).trim())
        const label = hasPath
          ? advanced
            ? ED.GAOJIWENBEN
            : ED.PEIYINWENBEN
          : advanced
            ? ED.GAOJIWENBEN
            : ED.WENBEN
        let s = `${label}：${first}`
        s = textToNullRef(s)
        return s
      }
      case EventID.TextDif:
      case EventID.TextDifEx:
      case EventID.TextDifEx2:
        return `${ED.WENZIXUANXIANG}：`
      case EventID.TextEnd:
        return ED.WENZIXUANXIANGEND
      case EventID.Ad:
        return ED.XIANSHIGUANGGAO
      case EventID.AdChoice: {
        const pos = parseInt(par[0] ?? '0', 10)
        const sub = pos === 0 ? ED.YIKANWANDEJUQING : ED.WEIKANWANDEJUQING
        return `：${sub}`
      }
      case EventID.AdEnd:
        return ED.XIANSHIGUANGGAOJIESHU
      case EventID.AutoPlay:
        return `${ED.AUTOBOFANGJVQING} ${par[0] === '1' ? '[开]' : '[关]'}`
      case EventID.QuickPlay:
        return `${ED.KUAIJINJVQING}${par[1] ?? ''}`
      case EventID.ValueInput:
        return `${ED.SHUZHISHURU}：${par[1] ?? ''}`
      case EventID.PromptMessage:
        return ED.TISHIXIAOXIKUANG
      case EventID.AirBubbleMode: {
        let open = ''
        if (parseInt(par[1] ?? '0', 10) === 0) open = ED.GUAN
        if (parseInt(par[1] ?? '0', 10) === 1) open = ED.KAI
        let keep = ''
        if (parseInt(par[2] ?? '0', 10) === 0) keep = ED.QINGCHU
        if (parseInt(par[2] ?? '0', 10) === 1) keep = ED.BAOLIU
        const id = parseInt(par[0] ?? '0', 10)
        const bx = par[3] ?? ''
        const by = par[4] ?? ''
        const aw = par[5] ?? ''
        const ah = par[6] ?? ''
        return (
          ED.QIPAOMOSHI +
          bubbleLayDescription(id) +
          ',' +
          open +
          ',' +
          keep +
          ',' +
          ED.QISHIWEIZHIX +
          bx +
          ',' +
          ED.QISHIWEIZHIY +
          by +
          ',' +
          ED.XIANSHIQUYUKUAN +
          aw +
          ',' +
          ED.XIANSHIQUYUGAO +
          ah
        )
      }
      case EventID.SendAirBubble: {
        const roleId = parseInt(par[11] ?? '0', 10)
        const persons = proj?.systemData?.bubblePersion?.PersonList ?? []
        const p = persons.find((x) => x.Id === roleId)
        const positon = p ? personLocationZh(p.Location as number) : ''
        const mt = parseInt(par[3] ?? '0', 10)
        let typ = ''
        if (mt === 1) typ = ED.ZI
        if (mt === 2) typ = ED.TU
        if (mt === 3) typ = ED.YIN
        const msg = par[6] ?? ''
        const tail =
          msg.length === 0 ? (mt === 2 ? par[12] ?? '' : '') : msg
        const id = parseInt(par[0] ?? '0', 10)
        return (
          ED.FASHEQIPAO +
          bubbleLayDescription(id) +
          ',' +
          positon +
          ',' +
          typ +
          ',' +
          tail
        )
      }
      case EventID.Comment: {
        let s = `${ED.ZHUSHI}：${firstLineCs(par[0] ?? '')}`
        s = textToNullRef(s)
        return s
      }
      case EventID.BookMark:
        return par[2] ?? ''
      case EventID.NotSelect: {
        let s = `${par[0] ?? ''}`
        s = textToNullRef(s)
        return s
      }
      case EventID.TextChoice: {
        const c = String(par[1] ?? '').trim()
        const marker = `ORGTIMEOVER|${ED.JIESHUXIANG}`
        if (c.indexOf(marker) !== -1) return `：${ED.DAOJISHIJVQING}`
        const pos = Math.max(0, parseInt(par[0] ?? '0', 10) || 0)
        const label = c || `选项${pos + 1}`
        return `[${label}] 的剧情`
      }
      case EventID.DisposeText:
        return ED.XIAOSHIDUIHUAKUANG
      case EventID.OpenUrl:
        return `${ED.DAKAIWANGYE}：${firstLineCs(par[0] ?? '')}`
      case EventID.DisableMenu:
        return `${ED.CAIDANGONGNENG}：`
      case EventID.FreshUI:
        return ED.SHUAXINUIJIEMIAN
      case EventID.ReturnGameUI: {
        let s = ED.FANHUIYOUXI
        if (par.length > 1 && par[1] === '1') {
          if (par[3] === '1') {
            const ver = parseInt(par[2] ?? '0', 10)
            s += ` 淡出 ${ver / 60}秒`
          } else {
            s += ` 淡出 ${par[2] ?? ''}帧`
          }
        }
        return s
      }
      case EventID.FloatControl:
        return `${ED.XUANFUZUJIANKAIGUAN}：${par[1] ?? ''}`
      case EventID.IF:
        return describeIf(par, code)
      case EventID.IFEnd:
        return `：${ED.FENQIEND}`
      case EventID.Loop:
        return ED.XUNHUAN
      case EventID.LoopAboveStart:
        return ED.YISHANGFANFU
      case EventID.ButtonDif:
        return `${ED.TUPIANXUANXIANG}：`
      case EventID.ButtonDifEnd:
        return ED.TUPIANXUANXIANGEND
      case EventID.JumpStory:
        return describeJumpStory(par, ED.TIAOZHUANJVQING)
      case EventID.JumpStoryUI:
        return describeJumpStory(par, ED.JUMP_UI)
      case EventID.Var:
        return `${ED.SHUZHICAOZUO}：${parseVariableOperandLabel(par)}`
      case EventID.BackTitle: {
        const fadeOut = par[0] === '1'
        const frame = parseInt(par[1] ?? '0', 10) || 0
        if (!fadeOut) return ED.FANHUIBIAOTIHUAMIAN
        return `${ED.FANHUIBIAOTIHUAMIAN} 淡出${frame}帧`
      }
      case EventID.LoopBreak:
        return `${ED.ZHONGDUANXUNHUAN}-${ED.CENGSHU}：${par[0] ?? ''}`
      case EventID.Wait: {
        const frames = Math.max(0, parseInt(par[0] ?? '0', 10) || 0)
        const value = par[1] === '1' ? Number((frames / 60).toFixed(1)) : frames
        const unit = par[1] === '1' ? '秒' : '帧'
        return `${ED.DENGDAI}：${value}${unit}`
      }
      case EventID.IfChoice:
        return `：${ED.CHUCIZHIWAIDEJVQING}：`
      case EventID.ButtonDifChoose: {
        const pos = Math.max(0, parseInt(par[0] ?? '0', 10) || 0)
        const directName = String(par[1] ?? '').trim()
        const buttons = proj?.systemData?.btnList?.buttons ?? []
        const name = directName || String(buttons[pos]?.Name ?? '').trim()
        const label = name || `选项${pos + 1}`
        return `[${label}]的剧情`
      }
      case EventID.VarsEx:
        return `${ED.ERZHOUMUSHUZHI}：${parseVariableOperandLabel(par, true)}`
      case EventID.CallMenu:
        return describeCallMenu(par)
      case EventID.String: {
        const idx = parseInt(par[0] ?? '0', 10)
        const isVarIndex = par[2] === '1'
        const stringNames = (proj?.gameStr?.Data ?? []).map((item) => item?.Name ?? '')
        const varNames = (proj?.gameVar?.Data ?? []).map((item) => item?.Name ?? '')
        const prefix = isVarIndex ? '数值指定字符串' : ED.ZIFUCHUAN
        const targetLabel = isVarIndex
          ? getNamedVariableLabel(idx, varNames, '数值')
          : getNamedVariableLabel(idx, stringNames, '字符串')
        return `${prefix}：${targetLabel} ="${par[1] ?? ''}"`
      }
      case EventID.SeniorVar: {
        const idxStr = par[0] ?? ''
        const label = idxStr.indexOf('EX') >= 0 ? ED.GAOJIERZHOUMU : ED.GAOJISHUZHI
        return `${label}：${seniorVarDesc(par)}`
      }
      case EventID.SeniorIf:
        return par[2] ?? ''
      case EventID.ForcedSaveRead: {
        const isRead = par[0] === '1'
        const p0 = isRead ? ED.QIANGZHIDUDANG : ED.QIANGZHICUNDANG
        const num = parseInt(par[1] ?? '0', 10)
        const isDel = par[2] === '1'
        if (isForcedSaveReadSlotValid(num, getEventDescriptionProject())) {
          const hao = ED.HAO
          if (isDel && isRead) {
            return `${p0}：${num}${hao} ${ED.DUHOUSHANCHU}`
          }
          return `${p0}：${num}${hao}`
        }
        return `${p0}：${ED.WUXIAO}`
      }
      case EventID.Bubble:
        return `${ED.QIPAOSHIXIAOGUO}：${par[1] ?? ''}`
      case EventID.CallSubStory:
        return describeCallSubStory(par)
      case EventID.Weather: {
        const weatherLabel = ['无天气效果', '刮风', '下雨', '下雪'][parseInt(par[0] ?? '0', 10)] ?? '无天气效果'
        return `${ED.TIANQI}：${weatherLabel}`
      }
      case EventID.Shake: {
        const intensity = par[0] ?? ''
        const speed = par[1] ?? ''
        const isFor = par[2] === '1'
        const shakeTime = Number(par[3] ?? '0') || 0
        const type = parseInt(par[5] ?? '0', 10)
        const tail = isFor
          ? ED.XUNHUAN
          : type === 0
            ? `${ED.SHIJIAN}：${shakeTime}${zhen}`
            : `${ED.SHIJIAN}：${shakeTime}${ED.MIAO}`
        return `${ED.ZHENDONG}：${ED.QIANGDU}：${intensity}, ${ED.SUDU}：${speed}, ${tail}`
      }
      case EventID.Flash: {
        const color = (par[0] ?? '').trim() || '255,255,255'
        const time = parseInt(par[1] ?? '0', 10)
        return `${ED.HUAMIANSHANSHUO}：${color}, ${time}${zhen}`
      }
      case EventID.PlanConvertScene:
        return ED.ZHUNBEIZHUANCHANG
      case EventID.ConvertSceneStart:
        return `${ED.KAISHIZHUANCHANG}：${par[0] ?? ''}`
      case EventID.UpateSceneHue: {
        const rgb = par[0] ?? ''
        const time = parseInt(par[2] ?? '0', 10)
        return `${ED.GENGGAICHANGJINGSEDIAO}：${rgb}, ${time}${zhen}`
      }
      case EventID.BGMAdd:
        return `${ED.CHARUBGMJIANSHANG} ${par[1] ?? ''}`
      case EventID.CGAdd:
        return `${ED.CHARUCGJIANSHANG} ${par[1] ?? ''}`
      case EventID.UnlockCollect:
        return `${ED.JIESUOSHOUJIXIANG}：${par[1] ?? ''}`
      case EventID.SimpleEffect:
        return `${ED.JIANYITEXIAO}：${simpleEffectLine(par)}`
      case EventID.PicShow:
        return describePicShow(par)
      case EventID.DressRole:
        return describeDressRole(par)
      case EventID.DisposePic:
        return `${ED.TUPIANXIAOCHU}：${ED.BIANHAO}：${par[0] ?? ''}`
      case EventID.MovePic:
        return describeMovePic(par, zhen)
      case EventID.MoodShow:
        return `${ED.XIANSHIXINQING}：${fileNameEx(par[1])}, ${par[5] ?? ''}${zhen}, X${par[2] ?? ''}, Y${par[3] ?? ''}, ${par[4] ?? ''}`
      case EventID.RotatePic:
        return describeRotatePic(par, zhen)
      case EventID.MaskPic:
        return `${ED.TPIANZHEZHAO}：${par[4] ?? ''}`
      case EventID.VitLock: {
        const v = par[0] ?? ''
        return `${ED.DANGQINATILIZHI}${v}`
      }
      case EventID.StoryLock:
        return describeStoryLock(par)
      case EventID.ResourceLoad:
        return `${ED.JIAZAIZIYUAN}：${par[0] ?? ''}`
      case EventID.GifPicShow:
        return describeGifOrOaf(par, false)
      case EventID.Oaf2:
        return describeGifOrOaf(par, true)
      case EventID.StartBGM: {
        const path = fileNameEx(par[0])
        const vol = par[1] ?? ''
        if (!path) return `${ED.BOFANGBEIJINGYINYUE}：${ED.NONE}`
        let s = `${ED.BOFANGBEIJINGYINYUE}：${path}, ${audioTrackLabel(par[8])}, ${yl}${vol} `
        s += audioFadeSuffix(par)
        return s.trimEnd()
      }
      case EventID.StartSE: {
        const path = fileNameEx(par[0])
        const vol = par[1] ?? ''
        if (!path) return `${ED.BOFANGYINXIAO}：${ED.NONE}`
        let s = `${ED.BOFANGYINXIAO}：${path}, ${audioTrackLabel(par[8])}, ${yl}${vol} `
        s += audioFadeSuffix(par)
        return s.trimEnd()
      }
      case EventID.StartVoice: {
        const path = fileNameEx(par[0])
        const vol = par[1] ?? ''
        if (!path) return `${ED.BOFANGYUYIN}：${ED.NONE}`
        let s = `${ED.BOFANGYUYIN}：${path}, ${audioTrackLabel(par[8])}, ${yl}${vol} `
        s += audioFadeSuffix(par)
        return s.trimEnd()
      }
      case EventID.StartBGS: {
        const path = fileNameEx(par[0])
        const vol = par[1] ?? ''
        if (!path) return `${ED.BOFANGXUNHUANYINXIAO}：${ED.NONE}`
        let s = `${ED.BOFANGXUNHUANYINXIAO}：${path}, ${audioTrackLabel(par[8])}, ${yl}${vol} `
        s += audioFadeSuffix(par)
        return s.trimEnd()
      }
      case EventID.FadeBGM: {
        const t = Number(par[0] ?? '0') || 0
        return `${ED.DANCHUBEIJINGYINYUE}：${audioTrackLabel(par[2])}, ${Number(t.toFixed(3))}${ED.MIAO}`
      }
      case EventID.StopSE: {
        const t = Number(par[0] ?? '0') || 0
        return `${ED.TINGZHIYINXIAO}：${audioTrackLabel(par[2])}, ${Number(t.toFixed(3))}${ED.MIAO}`
      }
      case EventID.StopVoice: {
        const t = Number(par[0] ?? '0') || 0
        return `${ED.TINGZHIYUYIN}：${audioTrackLabel(par[2])}, ${Number(t.toFixed(3))}${ED.MIAO}`
      }
      case EventID.FadeBGS: {
        const t = Number(par[0] ?? '0') || 0
        return `${ED.DANCHUBEIJINGYINXIAO}：${audioTrackLabel(par[2])}, ${Number(t.toFixed(3))}${ED.MIAO}`
      }
      default:
        return describeKnownEventName(Number(code), par)
    }
  } catch {
    return describeKnownEventName(Number(ev.Code), ev.Parameter ?? [])
  }
}
